import React, { useMemo } from 'react';

import type { UUID } from '@aline/common';
import { link, params } from '@aline/routes';
import { Typography } from '@mui/material';
import { Eye } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlineButton } from 'src/components';
import { getExternalPreviewTask } from 'src/components/workflow/workflow-detail/side-panel/task-review/task-review-helpers';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { useGetTaskWithComments } from 'src/hooks';
import { useFocusedView, useMarkExternalSession } from 'src/layouts/focused-view';
import { useCloseCurrentTab } from 'src/layouts/tabs/tab-context';
import { makeStyles } from 'tss-react/mui';

import { TaskPageLayout, TaskPageSkeleton } from './TaskPageLayout';

const useStyles = makeStyles()(theme => ({
  center: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  page: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
  },
  previewBanner: {
    alignItems: 'center',
    backgroundColor: '#FFFAEB',
    borderBottom: '1px solid #FEDF89',
    color: '#B54708',
    display: 'flex',
    flexShrink: 0,
    fontSize: 14,
    fontWeight: 600,
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(0.5, 2),
  },
  root: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
  },
}));

/** Where task emails land: the whole review, with its documents opening in their own tabs. */
export function TaskLandingPage(): JSX.Element {
  const { classes } = useStyles();
  const { taskId } = useParams<{ taskId: string }>();
  const { data, loading } = Apollo.useGetTaskLandingQuery({
    skip: !taskId,
    variables: { taskId: taskId ?? '' },
  });
  const [searchParams] = useSearchParams();
  const isPreviewRequested = searchParams.get(params.previewAsExternal) === '1';
  const task = data?.task;

  useFocusedView(
    getIsFocusedTaskPage({ isLoading: loading && !data, isPreviewRequested, task: task ?? null })
  );
  useMarkExternalSession(Boolean(task && !task.requesterIsInternal));

  if (loading && !data)
    return (
      <div className={classes.page}>
        <div className={classes.root}>
          <TaskPageSkeleton />
        </div>
      </div>
    );

  if (!taskId || !task)
    return (
      <div className={classes.center}>
        <Typography color="text.secondary">{`This task isn't available.`}</Typography>
      </div>
    );

  return (
    <TaskLandingPanel
      isPreviewRequested={isPreviewRequested}
      taskId={task.id}
      workflowId={task.workflow.id}
    />
  );
}

interface TaskLandingPanelProps {
  isPreviewRequested: boolean;
  taskId: UUID;
  workflowId: UUID;
}

function TaskLandingPanel({
  isPreviewRequested,
  taskId,
  workflowId,
}: TaskLandingPanelProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { loading, task } = useGetTaskWithComments(taskId);
  const isPreview = isPreviewRequested && Boolean(task?.requesterIsInternal);
  const panelTask = useMemo(
    () => (task && isPreview ? getExternalPreviewTask(task) : task),
    [isPreview, task]
  );
  const closeCurrentTab = useCloseCurrentTab();
  const exitPreview = (): void => {
    openLink(link.WorkflowDetailTabTask({ taskId, workflowId }), {
      applyRouteToExistingTab: true,
      title: task?.workflow.name || 'Workflow',
    });
    closeCurrentTab?.();
  };

  return (
    <div className={classes.page}>
      {isPreview && (
        <div className={classes.previewBanner}>
          <Eye size={16} />
          Preview of what external assignees see. Actions are turned off.
          <AlineButton color="primary" size="small" variant="text" onClick={exitPreview}>
            Exit preview
          </AlineButton>
        </div>
      )}
      <div className={classes.root}>
        {(loading || !task) && <TaskPageSkeleton />}
        {!loading && panelTask && (
          <TaskPageLayout
            isPreview={isPreview}
            task={panelTask}
            workflowId={workflowId}
            onOpenTask={id => navigate(link.TaskLanding({ taskId: id }))}
          />
        )}
      </div>
    </div>
  );
}

/**
 * External assignees, and internal users previewing as one, get the focused shell.
 * Null while the task is loading, so the shell keeps what it showed.
 */
function getIsFocusedTaskPage({
  isLoading,
  isPreviewRequested,
  task,
}: {
  isLoading: boolean;
  isPreviewRequested: boolean;
  task: Pick<Apollo.Task, 'requesterIsInternal'> | null;
}): boolean | null {
  if (isLoading) return null;
  // A task that failed to load does not show the viewer is internal, so the stripped shell stays.
  if (!task) return true;

  return !task.requesterIsInternal || isPreviewRequested;
}
